// EditSurvey.jsx – React 19 · onSubmit async puro · sin useTransition
import { use, useCallback, useMemo, Suspense } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { db } from '../../../config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  SharedSurveyForm,
  createInitialSurvey,
  updateSurvey,
  SurveyLoader,
  SurveyErrorBoundary,
} from './Survey';
import { Alert, Container } from '@mui/material';
import { translations } from './surveyTranslations';

import { deleteSurveyDraft } from './surveyLocalStorage.js';
import { createPromiseCache } from './cacheUtils';
import SubscriptionStatusAlert from './ui/SubscriptionStatusAlert';

// ─── Promise cache ─────────────────────────────────────────────────────────
// ← FIX: antes era un Map manual + makeCacheKey/evictSurveyCache escritos a
// mano. Ahora usa el mismo helper compartido que logoCache (Survey.js) y
// existsCache (CreateSurvey.jsx); solo se conserva la lógica de negocio
// propia de esta pantalla (la key compuesta y qué evictar).
const surveyCache = createPromiseCache();

const makeCacheKey = (surveyId, uid, subscriptionActive) =>
  `${surveyId}:${uid}:${subscriptionActive}`;

const evictSurveyCache = (surveyId, uid) => {
  const prefix = `${surveyId}:${uid}:`;
  surveyCache.evict((key) => key.startsWith(prefix));
};

function getSurveyPromise(surveyId, uid, subscriptionActive) {
  const key = makeCacheKey(surveyId, uid, subscriptionActive);
  return surveyCache.getOrCreate(key, () =>
    getDoc(doc(db, 'surveys', surveyId)).then((snap) => {
      if (!snap.exists()) throw new Error('not_found');
      const data = snap.data();
      if (data.urlId) deleteSurveyDraft(data.urlId);
      return createInitialSurvey({ ...data, id: surveyId }, uid, subscriptionActive);
    }),
  );
}

// ─── Inner component ──────────────────────────────────────────────────────
function EditSurveyInner({ surveyId, uid, subscriptionActive, currentUser }) {
  const initialSurvey = use(getSurveyPromise(surveyId, uid, subscriptionActive));

  const handleSubmit = useCallback(
    async (surveyData) => {
      const result = await updateSurvey(surveyId, surveyData);
      if (surveyData.urlId) deleteSurveyDraft(surveyData.urlId);
      // FIX Bug 2: evictar todas las variantes para traer datos frescos
      evictSurveyCache(surveyId, uid);
      return result;
    },
    [surveyId, uid],
  );

  return (
    <SharedSurveyForm
      key={surveyId}
      initialSurvey={initialSurvey}
      isEditing
      currentUser={currentUser}
      onSubmit={handleSubmit}
    />
  );
}

// ─── Componente público ───────────────────────────────────────────────────
const EditSurvey = ({ surveyId }) => {
  const {
    currentUser,
    language,
    isActive: subscriptionActive,
    subscription,           // objeto raw con cancel_at_period_end y current_period_end
  } = useAuth();

  const lang = language === 'es' ? 'es' : 'en';
  const t    = useMemo(() => translations[lang], [lang]);

  if (!currentUser?.uid || !surveyId) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <Alert severity="warning">
          {lang === 'es' ? 'Parámetros inválidos.' : 'Invalid parameters.'}
        </Alert>
      </Container>
    );
  }

  return (
    <SurveyErrorBoundary
      key={surveyId}
      lang={lang}
      errorMessage={
        lang === 'es'
          ? 'Encuesta no encontrada o error al cargar.'
          : 'Survey not found or failed to load.'
      }
      // FIX Bug 2: evictSurveyCache limpia todas las variantes al reintentar
      onRetry={() => evictSurveyCache(surveyId, currentUser.uid)}
    >
      {/* ← FIX Bug 3: mismo componente MUI compartido que CreateSurvey.jsx
          (antes este bloque de aviso vivía duplicado carácter por carácter). */}
      <SubscriptionStatusAlert
        subscriptionActive={subscriptionActive}
        subscription={subscription}
        lang={lang}
      />

      <Suspense fallback={<SurveyLoader t={t} />}>
        <EditSurveyInner
          surveyId={surveyId}
          uid={currentUser.uid}
          subscriptionActive={subscriptionActive}
          currentUser={currentUser}
        />
      </Suspense>
    </SurveyErrorBoundary>
  );
};

export default EditSurvey;